// src/pages/contentManagement/documents/documentList.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Button, Collapse, Divider, Form, Modal, Pagination, Table, Tag, Tooltip, Upload } from "antd";
import { FaTrash, FaChevronDown, FaFileAlt, FaInfoCircle } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./documentList.css";

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  documents: [],
  currentPage: 1,
  pageSize: 10,
  sortConfig: { key: "uploadedAt", direction: "desc" },
  showDeleteModal: false,
  deleteDocumentId: null
};

// Reducer for state management
const documentReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setDocuments":
      return { ...state, documents: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload };
    case "setDeleteModal":
      return {
        ...state,
        showDeleteModal: action.payload.show,
        deleteDocumentId: action.payload.documentId || null
      };
    case "removeDocument":
      return {
        ...state,
        documents: state.documents.filter((doc) => doc.id !== action.payload)
      };
    case "addDocument":
      return {
        ...state,
        documents: [action.payload, ...state.documents]
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1
      };
    default:
      return state;
  }
};

function DocumentManagement() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [state, dispatch] = useReducer(documentReducer, initialState);
  const [form] = Form.useForm();

  // Additional state for upload and notifications
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const { isLoading, searchTerm, filters, documents, currentPage, pageSize, sortConfig, showDeleteModal, deleteDocumentId } = state;

  // Helper to determine file type from filename
  const getFileType = useCallback((fileName) => {
    if (!fileName) return "Unknown";
    const extension = fileName.split(".").pop().toLowerCase();
    const typeMap = {
      pdf: "PDF",
      doc: "Word Doc",
      docx: "Word Doc",
      xls: "Excel",
      xlsx: "Excel",
      ppt: "PowerPoint",
      pptx: "PowerPoint",
      txt: "Text"
    };
    return typeMap[extension] || extension.toUpperCase();
  }, []);

  // Helper to format file size
  const formatFileSize = useCallback((bytes) => {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }, []);

  // Configuration objects
  const filtersConfig = useMemo(
    () => ({
      uploadDate: {
        label: "Upload Date",
        options: [
          { value: "newest", label: "Newest" },
          { value: "last7Days", label: "Last 7 Days" },
          { value: "last30Days", label: "Last 30 Days" },
          { value: "oldest", label: "Oldest" }
        ]
      },
      documentType: {
        label: "Document Type",
        options: [
          { value: "PDF", label: "PDF" },
          { value: "Word Doc", label: "Word Doc" },
          { value: "Excel", label: "Excel" },
          { value: "PowerPoint", label: "PowerPoint" },
          { value: "Text", label: "Text" }
        ]
      }
    }),
    []
  );

  // Date filtering utility
  const filterByDate = useCallback((items, dateFilter) => {
    if (!dateFilter) return items;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case "newest":
        return [...items].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt));
      case "oldest":
        return [...items].sort((a, b) => new Date(a.uploadedAt) - new Date(b.uploadedAt));
      case "last7Days": {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        return items.filter((item) => new Date(item.uploadedAt) >= sevenDaysAgo);
      }
      case "last30Days": {
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        return items.filter((item) => new Date(item.uploadedAt) >= thirtyDaysAgo);
      }
      default:
        return items;
    }
  }, []);

  // Global search function
  const globalSearch = useCallback(
    (items, term) => {
      if (!term?.trim()) return items;

      const searchLower = term.toLowerCase().trim();

      return items.filter((item) => {
        const searchFields = [
          item.fileName || "",
          getFileType(item.fileName) || "",
          formatFileSize(item.fileSize) || "",
          item.uploadedAt
            ? new Date(item.uploadedAt)
                .toLocaleDateString("en-GB", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : ""
        ];

        return searchFields.some((field) => field.toLowerCase().includes(searchLower));
      });
    },
    [getFileType, formatFileSize]
  );

  // Apply all filters and search
  const filteredData = useMemo(() => {
    let result = [...documents];

    // Apply date filter first (includes sorting)
    result = filterByDate(result, filters.uploadDate);

    // Apply document type filter
    if (filters.documentType) {
      result = result.filter((doc) => getFileType(doc.fileName) === filters.documentType);
    }

    // Apply global search
    result = globalSearch(result, searchTerm);

    // Apply sorting if no date filter is applied
    if (!filters.uploadDate && sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        if (key === "uploadedAt") {
          return direction === "asc" ? new Date(a.uploadedAt) - new Date(b.uploadedAt) : new Date(b.uploadedAt) - new Date(a.uploadedAt);
        }
        if (key === "fileSize") {
          return direction === "asc" ? (a.fileSize || 0) - (b.fileSize || 0) : (b.fileSize || 0) - (a.fileSize || 0);
        }
        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [documents, filters, searchTerm, filterByDate, globalSearch, sortConfig, getFileType]);

  // Get current page data
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  // Fetch Documents
  const fetchDocuments = useCallback(async () => {
    dispatch({ type: "setLoading", payload: true });
    try {
      const token = await getAuthToken();
      const response = await axios.get(API_ENDPOINTS.DOCUMENTS, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 && Array.isArray(response.data)) {
        dispatch({ type: "setDocuments", payload: response.data });
      } else {
        throw new Error("Unexpected API response format.");
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      showNotification("Failed to load documents", "error");
      dispatch({ type: "setDocuments", payload: [] });
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [showNotification]);

  // Handle File Selection
  const handleFileChange = useCallback(
    (file) => {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        "text/plain"
      ];

      if (!allowedTypes.includes(file.type)) {
        showNotification("Only document files (PDF, Word, Excel, PowerPoint, Text) are allowed.", "error");
        return false;
      }

      setSelectedFile(file);
      return false; // Prevent auto upload
    },
    [showNotification]
  );

  // Handle Upload
  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      showNotification("Please select a file.", "error");
      return;
    }

    setUploadLoading(true);

    try {
      const token = await getAuthToken();

      // Step 1: Get Pre-signed URL from the backend
      const preSignedResponse = await axios.post(
        `${API_ENDPOINTS.DOCUMENTS}/presigned-url`,
        {
          fileName: selectedFile.name,
          fileType: selectedFile.type
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (preSignedResponse.status !== 200) {
        throw new Error("Failed to get a pre-signed URL.");
      }

      const { url, id, s3Key } = preSignedResponse.data;

      // Step 2: Upload the file directly to S3 (no auth header needed)
      await axios.put(url, selectedFile, {
        headers: {
          "Content-Type": selectedFile.type
        }
      });

      // Step 3: Save metadata to the backend
      const metadataPayload = {
        id,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        s3Key: s3Key // s3Key comes back from the presigned-url response
      };

      const metadataResponse = await axios.post(`${API_ENDPOINTS.DOCUMENTS}/metadata`, metadataPayload, { headers: { Authorization: `Bearer ${token}` } });

      if (metadataResponse.status === 200 || metadataResponse.status === 201) {
        showNotification("Document uploaded successfully!", "success");
        setSelectedFile(null);
        form.resetFields();
        await fetchDocuments();
      } else {
        throw new Error("Failed to save file metadata.");
      }
    } catch (error) {
      console.error("Upload failed:", error.response?.data || error.message);
      showNotification(error.response?.data?.message || "Upload failed. Please try again.", "error");
    } finally {
      setUploadLoading(false);
    }
  }, [selectedFile, fetchDocuments, showNotification, form]);

  // Handle Cancel - Clear form
  const handleCancel = useCallback(() => {
    setSelectedFile(null);
    form.resetFields();
    showNotification("Form cleared.", "info");
  }, [form, showNotification]);

  // Handle Delete
  const confirmDelete = useCallback((id) => {
    dispatch({
      type: "setDeleteModal",
      payload: { show: true, documentId: id }
    });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteDocumentId) return;

    dispatch({ type: "setDeleteModal", payload: { show: false } });
    dispatch({ type: "setLoading", payload: true });

    try {
      const token = await getAuthToken();
      const response = await axios.delete(`${API_ENDPOINTS.DOCUMENTS}?id=${deleteDocumentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 || response.status === 204) {
        dispatch({ type: "removeDocument", payload: deleteDocumentId });
        showNotification("Document deleted successfully.", "success");
      } else {
        throw new Error("Failed to delete the document.");
      }
    } catch (error) {
      console.error("Error deleting the document:", error);
      showNotification("Failed to delete the document.", "error");
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [deleteDocumentId, showNotification]);

  const handleDeleteModalCancel = useCallback(() => {
    dispatch({ type: "setDeleteModal", payload: { show: false } });
  }, []);

  // Event handlers
  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: "setFilters", payload: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  // Get tag color based on document type
  const getDocumentTypeColor = useCallback((type) => {
    const colorMap = {
      PDF: "red",
      "Word Doc": "blue",
      Excel: "green",
      PowerPoint: "orange",
      Text: "default"
    };
    return colorMap[type] || "default";
  }, []);

  // Table columns configuration
  const columns = useMemo(
    () => [
      {
        title: "SL. NO.",
        render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
        width: 80
      },
      {
        title: "DOCUMENT NAME",
        dataIndex: "fileName",
        sorter: (a, b) => (a.fileName || "").localeCompare(b.fileName || ""),
        render: (fileName) => fileName || "-"
      },
      {
        title: "DOCUMENT TYPE",
        dataIndex: "fileName",
        sorter: (a, b) => (getFileType(a.fileName) || "").localeCompare(getFileType(b.fileName) || ""),
        render: (fileName) => {
          const type = getFileType(fileName);
          return type ? <Tag color={getDocumentTypeColor(type)}>{type}</Tag> : "-";
        },
        width: 150
      },
      {
        title: "DOCUMENT SIZE",
        dataIndex: "fileSize",
        sorter: (a, b) => (a.fileSize || 0) - (b.fileSize || 0),
        render: (size) => formatFileSize(size),
        width: 130
      },
      {
        title: "UPLOADED DATE",
        dataIndex: "uploadedAt",
        sorter: (a, b) => new Date(a.uploadedAt) - new Date(b.uploadedAt),
        render: (date) =>
          date
            ? new Date(date)
                .toLocaleDateString("en-GB", {
                  timeZone: "Asia/Kolkata",
                  day: "2-digit",
                  month: "short",
                  year: "numeric"
                })
                .replace(/ /g, "-")
            : "-"
      },
      {
        title: "ACTIONS",
        render: (_, record) => (
          <div className="docm-list-btn-group" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Delete">
              <button className="docm-list-delete-btn" onClick={() => confirmDelete(record.id)}>
                <FaTrash />
              </button>
            </Tooltip>
          </div>
        ),
        width: 120
      }
    ],
    [currentPage, pageSize, confirmDelete, getDocumentTypeColor, getFileType, formatFileSize]
  );

  // Excel export data
  const prepareExcelData = useCallback(() => {
    return filteredData.map((doc, index) => ({
      "SL. NO.": index + 1,
      "Document Name": doc.fileName,
      "Document Type": getFileType(doc.fileName),
      "Document Size": formatFileSize(doc.fileSize),
      "Uploaded Date": doc.uploadedAt
        ? new Date(doc.uploadedAt)
            .toLocaleDateString("en-GB", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "short",
              year: "numeric"
            })
            .replace(/ /g, "-")
        : "-"
    }));
  }, [filteredData, getFileType, formatFileSize]);

  // Effects
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return (
    <Layout
      navTitle="Content Management"
      pageTitle="Documents"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="docm-upload-section">
        <Collapse
          expandIcon={({ isActive }) => (
            <FaChevronDown
              style={{
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s"
              }}
            />
          )}
          className="docm-upload-collapse"
          items={[
            {
              key: "1",
              label: "Upload New Document",
              children: (
                <Form form={form} layout="vertical" className="docm-upload-form">
                  <Form.Item
                    label="Upload Document Resource"
                    name="documentFile"
                    tooltip={{
                      title: "Accepted formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT",
                      icon: <FaInfoCircle style={{ cursor: "pointer" }} />
                    }}
                  >
                    <Upload
                      className="docm-upload-input"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                      beforeUpload={handleFileChange}
                      fileList={selectedFile ? [selectedFile] : []}
                      onRemove={() => {
                        setSelectedFile(null);
                        form.setFieldsValue({ documentFile: undefined });
                      }}
                      maxCount={1}
                    >
                      <Button icon={<FaFileAlt />}>Select Document File</Button>
                    </Upload>
                  </Form.Item>

                  <Form.Item>
                    <div className="docm-form-btn-group">
                      <Button className="docm-form-cancel-btn" onClick={handleCancel} disabled={uploadLoading}>
                        Clear
                      </Button>
                      <Button className="docm-form-submit-btn" onClick={handleUpload} loading={uploadLoading} disabled={!selectedFile || uploadLoading}>
                        {uploadLoading ? "Uploading..." : "Upload"}
                      </Button>
                    </div>
                  </Form.Item>
                </Form>
              )
            }
          ]}
        />
      </div>

      <Divider />
      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search documents..."
            searchTerm={searchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="documents"
            excelSheetName="Documents"
          />
        }
      >
        <Table
          className="tpl-table"
          rowKey={(record) => record.id}
          rowClassName={(record, index) => (index % 2 === 0 ? "table-row-light" : "table-row-dark")}
          columns={columns}
          dataSource={currentPageData}
          loading={isLoading}
          pagination={false}
          expandable={{
            showExpandColumn: false
          }}
        />
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Confirm Deletion"
        open={showDeleteModal}
        onCancel={handleDeleteModalCancel}
        footer={[
          <div className="docm-modal-btn-group" key="modal-buttons">
            <Button className="docm-modal-cancel-btn" key="cancel" onClick={handleDeleteModalCancel}>
              Cancel
            </Button>
            <Button className="docm-modal-delete-btn" key="delete" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        ]}
        width={400}
        centered
      >
        <p>Are you sure you want to delete this document?</p>
      </Modal>
    </Layout>
  );
}

export default DocumentManagement;
